import { type SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { type FormTask, type FormValues, type RedtailVariant } from '../AddTaskToCRMModal'
import { Controller, useFormContext } from 'react-hook-form'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { type TableColumn } from 'react-data-table-component'
import DatePicker from 'react-datepicker'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { ZocksDataTable } from '../../../../../components/DataTable/ZocksDataTable'
import { useLazyGetIntegrationTeammatesQuery } from '../../../../../store/integrations/integrations'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { Badge, Form } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { type User } from '../../../../../models/User'
import { REDTAIL_ACTIVITY_TYPE_OPTIONS, REDTAIL_IMPORTANCE_MAP, reactSelectBaseStyles, taskCategoryEntityType, type ActivityTemplate } from './utils'
import { TrackEvent, useTracking } from '../../../../../apps/professional-app/fa/common/tracking'
import { IntegrationServiceId } from '../../../../../common/integrations'
import Select, { components, CSSObjectWithLabel, DropdownIndicatorProps, GroupBase, MultiValue, OptionsOrGroups, ValueContainerProps } from 'react-select'
import { type OptionProps } from 'react-select/dist/declarations/src/components/Option'
import './TaskTable.scss'
import AsyncSelect from 'react-select/async'
import { useDebouncedCallback } from 'use-debounce'

export interface TasksTableProps {
  serviceId: string
  isAllSelected: boolean
  anySelected: boolean
  onSelectAll: (checked: boolean) => void
  tasks: FormTask[]
  taskCategoryOptions: SelectOption[]
  assignedToOptions: SelectOption[]
  activityTemplateOptions: SelectOption[]
  activityTemplateMap: Map<number, ActivityTemplate>
  onEditTask: (taskId: string) => void
  onDeleteTask: (taskId: string) => void
  onTemplateMerge?: (taskId: string, templateId: number) => void
  onViewVariant?: (taskId: string) => void
  redtailVariants?: Record<string, RedtailVariant>
  editDisabled: boolean
}

export type TaskCategoryEntityTypeConfig = {
  api: string
  dto: string
}

const _STATUS_OPTIONS: SelectOption[] = [
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Closed' },
]

const _PRIORITY_OPTIONS: SelectOption[] = [
  { label: 'Low', id: 'Low' },
  { label: 'Medium', id: 'Medium' },
  { label: 'High', id: 'High' },
]

const _REDTAIL_IMPORTANCE_OPTIONS: SelectOption[] = [
  { label: 'Low', id: 'Low' },
  { label: 'Normal', id: 'Normal' },
  { label: 'High', id: 'High' },
]

const ReactSelectStyles = {
  ...reactSelectBaseStyles,
  menu: (base: CSSObjectWithLabel) => ({
    ...reactSelectBaseStyles.menu(base),
    minWidth: '0',
  }),
}

export const TasksTable: React.FC<TasksTableProps> = ({
  serviceId,
  isAllSelected,
  anySelected,
  onSelectAll,
  tasks,
  taskCategoryOptions,
  assignedToOptions,
  activityTemplateOptions,
  activityTemplateMap,
  onEditTask,
  onDeleteTask,
  onTemplateMerge,
  onViewVariant,
  redtailVariants,
  editDisabled,
}) => {
  const { control, setValue, getValues } = useFormContext<FormValues>()
  const selectAllCheckboxRef = React.useRef<HTMLInputElement>(null)
  const { tracked, trackingClient } = useTracking()
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = anySelected && !isAllSelected
    }
  }, [anySelected, isAllSelected])

  const handleTaskFieldUpdate = tracked(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    (field: any, value: unknown, type: string) => {
      field.onChange(value)
    },
    {
      eventName: TrackEvent.TASK_UPDATED,
    },
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventDetailsExtractor: (field: any, value: unknown, type: string) => {
        return {
          type,
        }
      },
    },
  )

  const onResetTask = useCallback(
    (rowIndex: number) => {
      const currentTask = getValues(`tasks.${rowIndex}`)
      const resetValues: Partial<FormTask> = {
        activityTemplateId: null,
        category: null,
        activityCodeId: serviceId === IntegrationServiceId.REDTAIL ? 4 : null,
        priority: serviceId === IntegrationServiceId.REDTAIL ? 'Normal' : 'Medium',
        importance: null,
      }
      setValue(`tasks.${rowIndex}`, { ...currentTask, ...resetValues }, { shouldDirty: true })
    },
    [getValues, serviceId, setValue],
  )

  const columns: TableColumn<FormTask & { id: string }>[] = [
    {
      name: (
        <Form.Check
          ref={selectAllCheckboxRef}
          id='select-all-tasks-header-checkbox'
          checked={isAllSelected}
          onChange={() => onSelectAll(!anySelected)}
          title='Select all'
        />
      ),
      width: '3rem',
      button: true,
      ignoreRowClick: true,
      selector: (row) => row.selected,
      cell: (row, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.selected`}
          control={control}
          render={({ field }) => <Form.Check id={`select-task-checkbox-${row.id}`} checked={field.value} onChange={field.onChange} />}
        />
      ),
    },
    {
      name: 'Task',
      wrap: true,
      grow: 2,
      cell: (row) => {
        const variant = redtailVariants?.[row.id]
        const displayName = variant ? variant.taskName : row.task
        return (
          <div className='d-flex flex-column'>
            <span>
              <strong>
                {displayName}
                {!variant && row.description ? ':' : ''}
              </strong>{' '}
              {!variant ? row.description : null}
            </span>
            {variant ? (
              <Badge
                bg='light'
                text='dark'
                role='button'
                tabIndex={0}
                className='mt-2 d-inline-flex align-items-center gap-1 border'
                style={{ fontSize: '0.85em' }}
                onClick={() => onViewVariant?.(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onViewVariant?.(row.id)
                  }
                }}
              >
                <i className='bi bi-file-earmark-text' />
                View description
              </Badge>
            ) : null}
          </div>
        )
      },
    },
    {
      name: 'Template',
      width: '12%',
      wrap: true,
      omit: serviceId !== IntegrationServiceId.REDTAIL,
      cell: (_, rowIndex) => (
        <Controller
          key={rowIndex}
          name={`tasks.${rowIndex}.activityTemplateId`}
          control={control}
          render={({ field }) => (
            <div className='w-100'>
              <ActivityTemplateColumnDropdown
                selectedTemplateId={field.value}
                activityTemplateOptions={activityTemplateOptions}
                onOptionSelected={(templateId) => {
                  if (templateId) {
                    const template = activityTemplateMap.get(templateId)
                    if (template) {
                      trackingClient.track({
                        eventName: TrackEvent.REDTAIL_TEMPLATE_SELECTED,
                      })
                      const currentTask = getValues(`tasks.${rowIndex}`)
                      handleTaskFieldUpdate(field, templateId, 'activityTemplateChanged')
                      const importanceLabel = template.importance ? REDTAIL_IMPORTANCE_MAP[template.importance] : undefined
                      const updates: Partial<FormTask> = {
                        activityTemplateId: templateId,
                        category: template.categoryId ? String(template.categoryId) : null,
                        activityCodeId: template.typeId ?? 4,
                        priority: importanceLabel ?? 'Normal',
                        importance: template.importance ?? null,
                      }

                      setValue(`tasks.${rowIndex}`, { ...currentTask, ...updates }, { shouldDirty: true })

                      if (onTemplateMerge) {
                        onTemplateMerge(currentTask.id, templateId)
                      }
                    }
                  } else {
                    handleTaskFieldUpdate(field, templateId, 'activityTemplateChanged')
                  }
                }}
              />
            </div>
          )}
        />
      ),
    },
    {
      name: 'Due date',
      wrap: true,
      width: '12%',
      cell: (_, rowIndex) => (
        <Controller
          key={rowIndex}
          name={`tasks.${rowIndex}.dueDate`}
          control={control}
          rules={{ required: 'Due date is required.' }}
          render={({ field, fieldState }) => (
            <div className={'task-date-row'}>
              <label htmlFor={`tasks-date-${rowIndex}`} className={'d-flex align-items-center '}>
                <i className={'bi bi-calendar ps-3'} />
                <DatePicker
                  id={`tasks-date-${rowIndex}`}
                  className='form-control border-0'
                  selected={field.value}
                  onChange={(date) => handleTaskFieldUpdate(field, date, 'dueDateChanged')}
                  todayButton='Today'
                  popperProps={{
                    strategy: 'fixed',
                  }}
                />
              </label>
              {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
            </div>
          )}
        />
      ),
    },
    {
      name: 'Assigned To',
      wrap: true,
      width: '11%',
      cell: (_, rowIndex) => (
        <Controller
          key={rowIndex}
          name={serviceId === IntegrationServiceId.REDTAIL ? `tasks.${rowIndex}.multipleAssignedTo` : `tasks.${rowIndex}.assignedTo`}
          control={control}
          rules={{ required: 'Assigned to is required.' }}
          render={({ field, fieldState }) => {
            return (
              <div className='d-flex flex-1 flex-column'>
                <AssignedToDropdown
                  serviceId={serviceId}
                  selectedAssignedToIds={field.value ?? []}
                  onOptionSelected={(assignedTo) => handleTaskFieldUpdate(field, assignedTo, 'assignedToChanged')}
                  initialOptions={assignedToOptions}
                  dropdownName={field.name}
                />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </div>
            )
          }}
        />
      ),
    },
    {
      name: serviceId === IntegrationServiceId.REDTAIL ? 'Importance' : 'Priority',
      width: '10%',
      wrap: true,
      cell: (_, rowIndex) => {
        const options = serviceId === IntegrationServiceId.REDTAIL ? _REDTAIL_IMPORTANCE_OPTIONS : _PRIORITY_OPTIONS
        return (
          <Controller
            key={rowIndex}
            name={`tasks.${rowIndex}.priority`}
            control={control}
            rules={{ required: serviceId === IntegrationServiceId.REDTAIL ? 'Importance is required.' : 'Priority is required.' }}
            render={({ field }) => (
              <div className='w-100'>
                <Select<SelectOption, false>
                  isSearchable={false}
                  options={options}
                  value={options.find((option) => option.id === field.value)}
                  onChange={(option) => handleTaskFieldUpdate(field, option?.id, 'priorityChanged')}
                  menuPortalTarget={document.body}
                  menuPlacement='auto'
                  placeholder={serviceId === IntegrationServiceId.REDTAIL ? 'Select importance' : 'Select priority'}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.label}
                  hideSelectedOptions={false}
                  styles={ReactSelectStyles}
                  components={{
                    DropdownIndicator: CustomDropdownIndicator,
                  }}
                />
              </div>
            )}
          />
        )
      },
    },
    {
      name: 'Category',
      width: '10%',
      wrap: true,
      omit: !((Object.keys(taskCategoryEntityType).includes(serviceId) && taskCategoryOptions.length > 1) || serviceId === 'smartoffice'),
      cell: (_, rowIndex) => (
        <Controller
          key={rowIndex}
          name={`tasks.${rowIndex}.category`}
          control={control}
          render={({ field }) => (
            <div className='w-100'>
              <CategoryColumnDropdown
                selectedTaskCategoryId={field.value}
                taskCategoryOptions={taskCategoryOptions}
                onOptionSelected={(category) => handleTaskFieldUpdate(field, category, 'categoryChanged')}
              />
            </div>
          )}
        />
      ),
    },
    {
      name: 'Activity Type',
      width: '10%',
      wrap: true,
      omit: serviceId !== IntegrationServiceId.REDTAIL,
      cell: (_, rowIndex) => (
        <Controller
          key={rowIndex}
          name={`tasks.${rowIndex}.activityCodeId`}
          control={control}
          render={({ field }) => (
            <div className='w-100'>
              <ActivityTypeColumnDropdown
                selectedActivityTypeId={field.value}
                onOptionSelected={(activityTypeId) => handleTaskFieldUpdate(field, activityTypeId, 'activityTypeChanged')}
              />
            </div>
          )}
        />
      ),
    },
    {
      name: 'Status',
      width: '10%',
      cell: (_, rowIndex) => (
        <Controller
          key={rowIndex}
          name={`tasks.${rowIndex}.status`}
          control={control}
          rules={{ required: 'Status is required.' }}
          render={({ field }) => (
            <div className='w-100'>
              <StatusColumnDropdown selectedTaskStatusId={field.value} taskStatusOptions={_STATUS_OPTIONS} onOptionSelected={field.onChange} />
            </div>
          )}
        />
      ),
    },
    {
      name: '',
      width: '6%',
      right: true,
      cell: (row, rowIndex) => (
        <div className='d-flex justify-content-center p-0 ps-2'>
          <Button onClick={() => onDeleteTask(row.id)} className='delete-task-button p-0' aria-label='Delete Task'>
            <i className='bi bi-trash' />
          </Button>
          {!editDisabled ? (
            <Button
              onClick={() => (redtailVariants?.[row.id] ? onViewVariant?.(row.id) : onEditTask(row.id))}
              className='edit-task-button p-0 ps-3'
              aria-label='Edit Task'
            >
              <i className='bi bi-pencil-square' />
            </Button>
          ) : null}
          {serviceId === IntegrationServiceId.REDTAIL ? (
            <Button onClick={() => onResetTask(rowIndex)} className='reset-task-button p-0 ps-3' aria-label='Reset Task'>
              <i className='bi bi-arrow-counterclockwise' />
            </Button>
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <div className='add-to-crm-modal-tasks-table'>
      <ZocksDataTable columns={columns} data={tasks} fixedHeader fixedHeaderScrollHeight='100%' />
    </div>
  )
}

interface CategoryColumnDropdownProps {
  selectedTaskCategoryId: string | null
  taskCategoryOptions: SelectOption[]
  onOptionSelected: (optionId: string | null) => void
}

const CategoryColumnDropdown: React.FC<CategoryColumnDropdownProps> = ({ selectedTaskCategoryId, taskCategoryOptions, onOptionSelected }) => {
  const selectedCategory = selectedTaskCategoryId ? (taskCategoryOptions.find((o) => o.id === selectedTaskCategoryId) ?? null) : null

  return (
    <Select<SelectOption, false>
      options={taskCategoryOptions}
      value={selectedCategory}
      onChange={(option) => {
        if (!option) {
          return
        }
        onOptionSelected(option.id)
      }}
      menuPortalTarget={document.body}
      menuPlacement='auto'
      placeholder={'Select…'}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.label}
      hideSelectedOptions={false}
      styles={ReactSelectStyles}
      components={{
        DropdownIndicator: CustomDropdownIndicator,
      }}
    />
  )
}

interface StatusColumnDropdownProps {
  selectedTaskStatusId: string | null
  taskStatusOptions: SelectOption[]
  onOptionSelected: (optionId: string | null) => void
}

interface ActivityTypeColumnDropdownProps {
  selectedActivityTypeId: number | null
  onOptionSelected: (activityTypeId: number | null) => void
}

const ActivityTypeColumnDropdown: React.FC<ActivityTypeColumnDropdownProps> = ({ selectedActivityTypeId, onOptionSelected }) => {
  const selectedOption = REDTAIL_ACTIVITY_TYPE_OPTIONS.find((o) => o.id === String(selectedActivityTypeId)) ?? null

  return (
    <Select<SelectOption, false>
      isSearchable={false}
      options={REDTAIL_ACTIVITY_TYPE_OPTIONS}
      value={selectedOption}
      onChange={(option) => {
        if (!option) {
          return
        }
        onOptionSelected(Number(option.id))
      }}
      menuPortalTarget={document.body}
      menuPlacement='auto'
      placeholder={'Select…'}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.label}
      hideSelectedOptions={false}
      styles={ReactSelectStyles}
      components={{
        DropdownIndicator: CustomDropdownIndicator,
      }}
    />
  )
}

interface ActivityTemplateColumnDropdownProps {
  selectedTemplateId: number | null
  activityTemplateOptions: SelectOption[]
  onOptionSelected: (templateId: number | null) => void
}

const ActivityTemplateColumnDropdown: React.FC<ActivityTemplateColumnDropdownProps> = ({ selectedTemplateId, activityTemplateOptions, onOptionSelected }) => {
  const selectedOption = selectedTemplateId ? (activityTemplateOptions.find((o) => o.id === String(selectedTemplateId)) ?? null) : null

  return (
    <Select<SelectOption, false>
      isSearchable
      options={activityTemplateOptions}
      value={selectedOption}
      onChange={(option) => {
        if (!option) {
          return
        }
        onOptionSelected(option.id ? Number(option.id) : null)
      }}
      menuPortalTarget={document.body}
      menuPlacement='auto'
      placeholder={'Select…'}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.label}
      hideSelectedOptions={false}
      styles={ReactSelectStyles}
      components={{
        DropdownIndicator: CustomDropdownIndicator,
      }}
    />
  )
}

const StatusColumnDropdown: React.FC<StatusColumnDropdownProps> = ({ selectedTaskStatusId, taskStatusOptions, onOptionSelected }) => {
  const [selectedStatus, setSelectedStatus] = useState<SelectOption | null>(() =>
    selectedTaskStatusId ? (taskStatusOptions.find((o) => o.id === selectedTaskStatusId) ?? null) : null,
  )

  return (
    <Select<SelectOption, false>
      isSearchable={false}
      options={taskStatusOptions}
      value={selectedStatus}
      onChange={(option) => {
        if (!option) {
          return
        }
        setSelectedStatus(option.id === null ? null : option)
        onOptionSelected(option.id)
      }}
      menuPortalTarget={document.body}
      menuPlacement='auto'
      placeholder={'Select…'}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.label}
      hideSelectedOptions={false}
      styles={ReactSelectStyles}
      components={{
        DropdownIndicator: CustomDropdownIndicator,
      }}
    />
  )
}

interface AssignedToDropdownProps {
  serviceId: string
  selectedAssignedToIds: string | string[] | null
  onOptionSelected: (optionId: string[] | string | null) => void
  initialOptions: SelectOption[]
  dropdownName: string
}

const AssignedToDropdown: React.FC<AssignedToDropdownProps> = ({ serviceId, selectedAssignedToIds, onOptionSelected, initialOptions, dropdownName }) => {
  const enableBackendSearchServiceIds = ['salesforce', 'practifi', 'xlr8', 'smartoffice', 'elements', 'blackdiamond', 'dynamics', 'engage']
  const accountId = useAppSelector(selectAccountId)
  const [triggerGetIntegrationTeammatesQuery] = useLazyGetIntegrationTeammatesQuery()
  const [availableOptions, setAvailableOptions] = useState<SelectOption[]>([...initialOptions])

  const getAssignedToOptions = useCallback(
    (users: User[]) => {
      return users.map((user) => {
        const contactIntegration = user.integrations?.find((i) => i.serviceId === serviceId)
        const contactLabel = `${user.name ? user.name : ''}${user.email ? ` (${user.email})` : ''}`
        return { id: contactIntegration!.externalId, label: contactLabel }
      })
    },
    [serviceId],
  )

  const handleBackendSearchInputChange = useCallback(
    (searchInput: string, callback: (options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>) => void) => {
      try {
        triggerGetIntegrationTeammatesQuery({
          accountId,
          connector: serviceId,
          filters: { name: searchInput },
        })
          .unwrap()
          .then((response) => {
            if (response && response.data) {
              const options = getAssignedToOptions(response.data)
              setAvailableOptions(options)
              callback(options)
            }
          })
      } catch (e) {
        console.error('Failed to search teammates:', e)
      }
    },
    [accountId, serviceId, triggerGetIntegrationTeammatesQuery, getAssignedToOptions],
  )

  const debouncedLoader = useDebouncedCallback(handleBackendSearchInputChange, 400)

  const selectedOption = useMemo(
    () => availableOptions.find((option) => option.id === selectedAssignedToIds) ?? null,
    [availableOptions, selectedAssignedToIds],
  )

  if (serviceId === IntegrationServiceId.REDTAIL) {
    const currentSelectedIds = Array.isArray(selectedAssignedToIds) ? selectedAssignedToIds : selectedAssignedToIds ? [selectedAssignedToIds] : []

    return (
      <MultiSelectAssignedToDropdown
        serviceId={serviceId}
        selectedAssignedToIds={currentSelectedIds}
        onOptionSelected={onOptionSelected}
        initialOptions={initialOptions}
        dropdownName={dropdownName}
      />
    )
  }

  return (
    <>
      {enableBackendSearchServiceIds.includes(serviceId) ? (
        <AsyncSelect<SelectOption, false>
          cacheOptions
          defaultOptions={availableOptions}
          value={selectedOption}
          onChange={(option) => {
            if (!option) {
              return
            }
            onOptionSelected(option.id)
          }}
          loadOptions={debouncedLoader}
          menuPortalTarget={document.body}
          menuPlacement='auto'
          placeholder={'Select assignee…'}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.label}
          hideSelectedOptions={false}
          styles={ReactSelectStyles}
        />
      ) : (
        <Select<SelectOption, false>
          options={initialOptions}
          value={selectedOption}
          onChange={(option) => {
            if (!option) {
              return
            }
            onOptionSelected(option.id)
          }}
          menuPortalTarget={document.body}
          menuPlacement='auto'
          placeholder={'Select assignee…'}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.label}
          hideSelectedOptions={false}
          styles={ReactSelectStyles}
          components={{
            DropdownIndicator: CustomDropdownIndicator,
          }}
        />
      )}
    </>
  )
}

const MultiSelectAssignedToDropdown: React.FC<AssignedToDropdownProps> = ({ selectedAssignedToIds, onOptionSelected, initialOptions }) => {
  const selectedOptions = useMemo(() => {
    return initialOptions.filter((o) => selectedAssignedToIds?.includes(o.id))
  }, [selectedAssignedToIds, initialOptions])

  const handleOnChange = (options: MultiValue<SelectOption>) => {
    onOptionSelected(options.map((o) => o.id))
  }

  return (
    <Select<SelectOption, true>
      options={initialOptions}
      value={selectedOptions}
      onChange={handleOnChange}
      isMulti
      menuPortalTarget={document.body}
      menuPlacement='auto'
      placeholder={'Select assignee'}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.label}
      components={{
        Option: CustomOption,
        ValueContainer: CustomValueContainer,
        DropdownIndicator: CustomDropdownIndicator,
        MultiValue: () => null,
      }}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      styles={ReactSelectStyles}
    />
  )
}

const CustomDropdownIndicator = (props: DropdownIndicatorProps<SelectOption>) => {
  const { menuIsOpen } = props.selectProps

  return (
    <components.DropdownIndicator {...props}>
      {menuIsOpen ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}
    </components.DropdownIndicator>
  )
}

const CustomValueContainer = (props: ValueContainerProps<SelectOption>) => {
  const selected = (props.selectProps.value || []) as MultiValue<SelectOption>
  return (
    <components.ValueContainer {...props}>
      <div className='d-flex align-items-center'>
        {selected.length && !props.selectProps.inputValue.length
          ? `${selected.length > 1 ? `${selected.length} assignees selected` : selected[0].label}`
          : null}{' '}
        {props.children}
      </div>
    </components.ValueContainer>
  )
}
const CustomOption = (option: OptionProps<SelectOption>) => {
  const handleOnClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    option.selectOption(option.data)
  }
  return (
    <components.Option {...option}>
      <div onClick={handleOnClick} className='multi-select-option'>
        <Form.Check
          className='multi-select-option'
          key={option.data.id}
          type='checkbox'
          id={`option_${option.data.id}`}
          label={option.label}
          onChange={() => null}
          checked={option.isSelected}
          value={option.data.id}
        />
      </div>
    </components.Option>
  )
}
